"use client";

import { useCallback, useEffect, useMemo, useState } from "react";
import { AnimatePresence, motion } from "framer-motion";
import RemoveBgUpload from "@/components/removebg/RemoveBgUpload";
import RemoveBgPreview from "@/components/removebg/RemoveBgPreview";
import RemoveBgDownload from "@/components/removebg/RemoveBgDownload";
import RemoveBgHistory from "@/components/removebg/RemoveBgHistory";
import RemoveBgIllustration from "@/components/removebg/RemoveBgIllustration";
import RemoveBgScanning from "@/components/removebg/RemoveBgScanning";
import type { RemoveBgJob, RemoveBgUploadResult } from "@/types/removebg";
import { saveBackgroundRemovalJob } from "@/services/backgroundRemoval.service";
import { showToast } from "@/components/ui/Toast";
import { useUser } from "@/context/UserContext";

export default function RemoveBgPageClient() {
  const { user } = useUser();
  const isAuthenticated = Boolean(user);

  const [currentJob, setCurrentJob] = useState<RemoveBgJob | null>(null);
  const [originalPreviewUrl, setOriginalPreviewUrl] = useState<string | null>(null);
  const [historyRefreshKey, setHistoryRefreshKey] = useState(0);
  const [isSavingCache, setIsSavingCache] = useState(false);
  const [isProcessing, setIsProcessing] = useState(false);
  const [uploadedFile, setUploadedFile] = useState<File | null>(null);

  useEffect(() => {
    return () => {
      if (originalPreviewUrl?.startsWith("blob:")) {
        URL.revokeObjectURL(originalPreviewUrl);
      }
    };
  }, [originalPreviewUrl]);

  const handleUploadComplete = useCallback(async ({ job, file }: RemoveBgUploadResult) => {
    const nextUrl = URL.createObjectURL(file);
    setUploadedFile(file);
    setOriginalPreviewUrl((previous) => {
      if (previous?.startsWith("blob:")) URL.revokeObjectURL(previous);
      return nextUrl;
    });

    // Iniciar processamento
    setIsProcessing(true);

    // Simular tempo de processamento (3 segundos)
    setTimeout(() => {
      setCurrentJob(job);
      setIsProcessing(false);
    }, 3000);

    setIsSavingCache(true);
    try {
      await saveBackgroundRemovalJob(job, isAuthenticated);
      showToast(
        isAuthenticated
          ? "Fundo removido e salvo no seu histórico!"
          : "Fundo removido com sucesso!",
        "success"
      );
    } catch (error) {
      console.warn("Falha ao salvar job", error);
      showToast("Não foi possível salvar no histórico.", "warning");
    } finally {
      setIsSavingCache(false);
      setHistoryRefreshKey((value) => value + 1);
    }
  }, [isAuthenticated]);

  const handleSelectHistoryJob = useCallback((job: RemoveBgJob) => {
    setCurrentJob(job);
    setOriginalPreviewUrl((previous) => {
      if (previous?.startsWith("blob:")) URL.revokeObjectURL(previous);
      return job.url_original ?? null;
    });
    setIsProcessing(false);
    setUploadedFile(null);
  }, []);

  const handleUploadError = useCallback((message: string) => {
    showToast(message, "error");
  }, []);

  const handleSendAnotherImage = useCallback(() => {
    setCurrentJob(null);
    setOriginalPreviewUrl((previous) => {
      if (previous?.startsWith("blob:")) URL.revokeObjectURL(previous);
      return null;
    });
    setIsProcessing(false);
    setUploadedFile(null);
  }, []);

  const hasActiveJob = useMemo(() => Boolean(currentJob), [currentJob]);
  const showUpload = !isProcessing && !hasActiveJob;

  return (
    <section className="mx-auto w-full max-w-6xl px-4 py-12 sm:px-6 sm:py-16 lg:py-20">
      <div className="mx-auto flex max-w-3xl flex-col items-center text-center">
        <span className="inline-flex items-center rounded-full bg-brand-primary/20 px-4 py-1 text-xs font-semibold uppercase tracking-widest text-brand-secondary">
          IA ARTES TURBO
        </span>
        <h1 className="mt-6 text-2xl font-semibold text-white sm:text-3xl md:text-4xl">Removedor de Fundo Inteligente</h1>
        <p className="mt-4 max-w-2xl text-sm text-white/70 sm:text-base md:text-lg">
          Faça upload de uma imagem, visualize o recorte em tempo real e baixe o resultado em alta definição mantendo o visual do site.
        </p>
      </div>

      <div className="mt-8 space-y-8 sm:mt-12 sm:space-y-12">
        <AnimatePresence>
          {showUpload && (
            <motion.div
              key="upload"
              initial={{ opacity: 0, y: 16 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: -16 }}
              transition={{ duration: 0.25 }}
            >
              <RemoveBgUpload onComplete={handleUploadComplete} onError={handleUploadError} isPersisting={isSavingCache} />
            </motion.div>
          )}
        </AnimatePresence>

        <AnimatePresence>
          {isProcessing && uploadedFile && originalPreviewUrl && (
            <motion.div
              key="processing"
              initial={{ opacity: 0, y: 16 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: -16 }}
              transition={{ duration: 0.25 }}
              className="rounded-3xl bg-white/5 p-8 shadow-soft backdrop-blur-xl"
            >
              <RemoveBgScanning imageUrl={originalPreviewUrl} filename={uploadedFile.name} />
            </motion.div>
          )}
        </AnimatePresence>

        <AnimatePresence>
          {hasActiveJob && !isProcessing ? (
            <motion.div
              key={currentJob?.job_id}
              initial={{ opacity: 0, y: 16 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: -16 }}
              transition={{ duration: 0.25 }}
              className="space-y-10 rounded-3xl bg-white/5 p-8 shadow-soft backdrop-blur-xl"
            >
              {currentJob ? (
                <>
                  <RemoveBgPreview
                    job={currentJob}
                    originalSource={originalPreviewUrl ?? currentJob.url_original}
                    onSendAnotherImage={handleSendAnotherImage}
                  />
                  <RemoveBgDownload job={currentJob} />
                </>
              ) : (
                <RemoveBgIllustration />
              )}
            </motion.div>
          ) : !isProcessing && !showUpload && (
            <motion.div
              key="removebg-empty"
              initial={{ opacity: 0, y: 16 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.25 }}
              className="rounded-3xl bg-white/5 p-12 text-center shadow-soft backdrop-blur-xl"
            >
              <RemoveBgIllustration />
            </motion.div>
          )}
        </AnimatePresence>

        <RemoveBgHistory onSelectJob={handleSelectHistoryJob} activeJobId={currentJob?.job_id ?? null} refreshKey={historyRefreshKey} />
      </div>
    </section>
  );
}
